import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { authService } from "@/core/services/authService";
import { viewAsService } from "@/core/services/viewAsService";
import type { ClubMembership, ClubMembershipRelationship, EmailCheckResponse, UserInfo } from "@/core/types";
import { handleApiError } from "@/core/api/handleApiError";
import { getLogger } from "@/utils/logger";
import type { RestSubscriptionStatus } from "@/types/subscription";
import { isViewAsMemberEnabled } from "@/composables/useViewAsMember";
import { appHref } from "@/utils/appBase";

const logger = getLogger("Auth");

// For debugging
let checkAuthCount = 0;
let authCheckVersion = 0;

type UserHomeState =
  | { type: "member-dashboard" }
  | { type: "manager-club-redirect"; clubShortName: string }
  | { type: "manager-club-list"; managedClubs: ClubMembership[] }
  | { type: "manager-unactivated-home"; clubs: ClubMembership[] }
  | { type: "inactive-member-home"; clubs: ClubMembership[] }
  | { type: "find-clubs-redirect" };

export const FORCED_PERSONAL_DASHBOARD_QUERY_PARAM = "personalDashboard";

const USER_HOME_ACCESSIBLE_STATUSES = new Set<RestSubscriptionStatus>([
  "trial",
  "active",
  "cancelling",
  "grace_period",
]);
const USER_HOME_MANAGEABLE_RELATIONSHIPS: ClubMembershipRelationship[] = ["admin", "manager", "officer"];

function fallbackRelationship(membership: ClubMembership, isAdmin = false): ClubMembershipRelationship | null {
  if (isAdmin) return "admin";
  if (membership.relationship) return membership.relationship;
  if (membership.isManager) return "manager";
  if (membership.isOfficer) return "officer";
  if (membership.isMember) return "member";
  if (membership.isGuest) return "guest";
  return null;
}

function hasClubAccess(subscriptionStatus?: RestSubscriptionStatus): boolean {
  return subscriptionStatus !== undefined && USER_HOME_ACCESSIBLE_STATUSES.has(subscriptionStatus);
}

function isMembershipActivated(membership: ClubMembership): boolean {
  if (membership.isActivated !== undefined) return membership.isActivated;
  return membership.subscriptionStatus !== undefined && membership.subscriptionStatus !== "not_subscribed";
}

function canManageClubWorkspace(
  membership: ClubMembership,
  isAdmin = false,
): membership is ClubMembership & { relationship: ClubMembershipRelationship } {
  const relationship = fallbackRelationship(membership, isAdmin);
  return relationship !== null && USER_HOME_MANAGEABLE_RELATIONSHIPS.includes(relationship);
}

function hasMemberDashboardAccess(userInfo?: UserInfo): boolean {
  const memberships = userInfo?.clubMemberships ?? [];
  const currentUserIsAdmin = userInfo?.isAdmin ?? false;
  const nonGuestMemberships = memberships.filter(
    (membership) => fallbackRelationship(membership, currentUserIsAdmin) !== "guest",
  );

  return nonGuestMemberships.some((membership) => {
    const relationship = fallbackRelationship(membership, currentUserIsAdmin);
    return (
      relationship !== null &&
      ["admin", "officer", "member"].includes(relationship) &&
      hasClubAccess(membership.subscriptionStatus)
    );
  });
}

export function isInactiveManagedClubPersonalDashboardEligible(userInfo?: UserInfo): boolean {
  if (!userInfo) return false;
  if (userInfo.accountMode === "club_only") return false;
  if (hasMemberDashboardAccess(userInfo)) return false;

  const currentUserIsAdmin = userInfo.isAdmin ?? false;
  const managedClubs = (userInfo.clubMemberships ?? []).filter((membership) =>
    canManageClubWorkspace(membership, currentUserIsAdmin),
  );

  return managedClubs.length > 0 && managedClubs.every((membership) => !hasClubAccess(membership.subscriptionStatus));
}

export const useAuthStore = defineStore("auth", () => {
  // State
  // `rawUser` is the raw server-supplied UserInfo. Public consumers read `user`
  // (a masked computed below) so the View-as-Member toggle can transparently
  // hide officer/admin privileges in the UI without rewriting every caller.
  const rawUser = ref<UserInfo | undefined>(undefined);
  const isAuthenticated = ref(false);
  const isLoading = ref(false);
  const error = ref<string | undefined>(undefined);
  const authCheckFailed = ref(false);
  const initialAuthCheckDone = ref(false);
  const emailCheckResult = ref<EmailCheckResponse | undefined>(undefined);

  // Public `user`: when View-as-Member is ON, returns a clone with isAdmin
  // forced false and every clubMembership flag rewritten to member-only.
  // This is the single intercept that makes the toggle work app-wide.
  // Also strips `officerRoles` (consumed by UserHome district cards) and
  // per-membership `roles` (consumed by UserProfile) so officer-only UI
  // driven by those fields stays hidden in member view.
  const user = computed<UserInfo | undefined>(() => {
    const raw = rawUser.value;
    if (!raw) return undefined;
    if (!isViewAsMemberEnabled.value) return raw;
    return {
      ...raw,
      isAdmin: false,
      officerRoles: undefined,
      clubMemberships: raw.clubMemberships?.map((m) => {
        // A membership is privileged if booleans say so OR the relationship
        // string itself says so — relationship can carry "manager"/"officer"/
        // "admin" even when the booleans aren't perfectly aligned, so we have
        // to consult both, otherwise privilege leaks through the toggle.
        const wasPrivileged =
          m.isManager ||
          m.isOfficer ||
          m.relationship === "admin" ||
          m.relationship === "officer" ||
          m.relationship === "manager";
        const wasMember = m.isMember || m.relationship === "member";
        const wasGuest = m.isGuest || m.relationship === "guest";
        const maskedIsMember = wasPrivileged || wasMember;
        const maskedIsGuest = !maskedIsMember && wasGuest;
        return {
          ...m,
          isOfficer: false,
          isManager: false,
          isMember: maskedIsMember,
          isGuest: maskedIsGuest,
          roles: [],
          relationship: (maskedIsMember ? "member" : maskedIsGuest ? "guest" : "member") as ClubMembershipRelationship,
        };
      }),
    };
  });

  // Visibility for the View-as-Member menu entry. MUST derive from `rawUser`,
  // never from the masked `user` — otherwise toggling ON makes the menu entry
  // disappear and the user is trapped in member view.
  // Privilege detection mirrors the user-mask above: a membership counts as
  // privileged if the booleans say so OR the relationship string itself does.
  // Otherwise relationship-only officers/managers can never enable the toggle.
  const canViewAsMember = computed(() => {
    const r = rawUser.value;
    if (!r) return false;
    if (r.isAdmin) return true;
    return (r.clubMemberships ?? []).some(
      (m) =>
        m.isOfficer ||
        m.isManager ||
        m.relationship === "admin" ||
        m.relationship === "officer" ||
        m.relationship === "manager",
    );
  });

  // Read-only view-as (impersonation) state. All derived from `rawUser` so the banner
  // is visible even when the View-as-Member toggle is masking `user`.
  // See `docs/spec/admin/read-only-view-as.md`.
  const viewingAs = computed(() => rawUser.value?.viewingAs === true);
  // A marked session whose identity swap did not apply. Writes are blocked but the
  // identity is still the admin's, so the banner must say so instead of claiming a
  // session that is not in effect.
  const viewAsFailed = computed(() => rawUser.value?.viewAsFailed === true);
  const viewAsAdminEmail = computed(() => rawUser.value?.viewAsAdminEmail ?? "");
  // Identity of the account being viewed. Sent for both the working and the failed
  // case; the numeric id only for the working one, where the target resolved.
  const viewAsTargetEmail = computed(() => rawUser.value?.viewAsTargetEmail ?? "");
  const viewAsTargetId = computed(() => rawUser.value?.viewAsTargetId);

  // Getters
  const isAdmin = computed(() => user.value?.isAdmin || false);
  const isBetaTester = computed(() => user.value?.betaTester || false);
  const hasBetaAccess = computed(() => user.value?.isAdmin || user.value?.betaTester || false);
  const userClubs = computed(() => user.value?.clubs || []);
  const userName = computed(() => user.value?.fullName || "");
  const userHomeState = computed<UserHomeState>(() => {
    const memberships = user.value?.clubMemberships ?? [];
    const currentUserIsAdmin = user.value?.isAdmin ?? false;
    const isClubOnlyAccount = user.value?.accountMode === "club_only";
    const nonGuestMemberships = memberships.filter(
      (membership) => fallbackRelationship(membership, currentUserIsAdmin) !== "guest",
    );

    const hasMemberDashboard = hasMemberDashboardAccess(user.value);

    if (hasMemberDashboard) {
      return { type: "member-dashboard" };
    }

    const managedClubs = nonGuestMemberships.filter((membership) =>
      canManageClubWorkspace(membership, currentUserIsAdmin),
    );
    const activatedManagedClubs = managedClubs.filter((membership) => isMembershipActivated(membership));

    if (isClubOnlyAccount && managedClubs.length > 0 && activatedManagedClubs.length === 0) {
      return {
        type: "manager-unactivated-home",
        clubs: managedClubs,
      };
    }

    if (managedClubs.length === 1) {
      return {
        type: "manager-club-redirect",
        clubShortName: managedClubs[0].club.shortName,
      };
    }

    if (managedClubs.length > 1) {
      return {
        type: "manager-club-list",
        managedClubs,
      };
    }

    const inactiveMemberClubs = nonGuestMemberships.filter((membership) => {
      const relationship = fallbackRelationship(membership, currentUserIsAdmin);
      return relationship === "member" && !hasClubAccess(membership.subscriptionStatus);
    });

    if (inactiveMemberClubs.length > 0) {
      return {
        type: "inactive-member-home",
        clubs: inactiveMemberClubs,
      };
    }

    // A user whose only remaining relationships are lapsed memberships still has
    // a "Previous clubs" history to show. Route them to the member dashboard
    // (active list shows the empty state + Find a Club CTA) rather than bouncing
    // them straight to find-a-club, which would hide that history behind a redirect.
    const hasPreviousClubs = memberships.some((membership) => membership.isExpired);
    if (hasPreviousClubs) {
      return { type: "member-dashboard" };
    }

    return { type: "find-clubs-redirect" };
  });

  // Actions
  async function checkAuth(force = false) {
    // Skip if we're already loading or if we've already done a check and it's not forced
    if ((isLoading.value || initialAuthCheckDone.value) && !force) {
      logger.info(
        `🔒 Auth check skipped. Already loading or initial check done. ${isLoading.value}, initialCheckDone: ${initialAuthCheckDone.value}`,
      );
      return;
    }

    const requestVersion = ++authCheckVersion;
    checkAuthCount++;
    logger.info(`🔒 Auth check #${checkAuthCount} started. Current state:`, {
      isAuthenticated: isAuthenticated.value,
      hasUser: !!rawUser.value,
      initialCheckDone: initialAuthCheckDone.value,
    });

    isLoading.value = true;
    error.value = undefined;
    authCheckFailed.value = false;

    try {
      const userData = await authService.getCurrentUser();

      if (requestVersion !== authCheckVersion) return;

      if (userData && userData.email) {
        // We have user data - user is authenticated
        rawUser.value = userData;

        // If authentication state changed, log it
        if (!isAuthenticated.value) {
          logger.info("🔓 Auth state changing from unauthenticated to authenticated");
        }

        isAuthenticated.value = true;

        if (userData.requiresProfileCompletion) {
          logger.info("⚠️ User authenticated but profile completion is still required", {
            accountMode: userData.accountMode,
          });
        }

        logger.info("👤 User authenticated:", userData);
      } else {
        // No user data - user is not authenticated

        // If authentication state changed, log it
        if (isAuthenticated.value) {
          logger.info("🔒 Auth state changing from authenticated to unauthenticated");
        }

        rawUser.value = undefined;
        isAuthenticated.value = false;
        logger.info("👤 No user data found, not authenticated");
      }
    } catch (err) {
      if (requestVersion !== authCheckVersion) return;

      // A transport failure does not prove that the session is gone. Preserve any
      // resolved auth state and leave an unresolved initial check distinguishable
      // from the expected anonymous 401 path.
      authCheckFailed.value = true;
      // Auth verification is a background read. Keep it silent here; the guard
      // retries once and protected navigation remains unresolved if it still fails.
      handleApiError(err, { fallbackMessage: "Failed to verify authentication status", silent: true });
      error.value = "Failed to verify authentication status";
    } finally {
      if (requestVersion === authCheckVersion) {
        isLoading.value = false;
        initialAuthCheckDone.value = true;
        logger.info(`🔒 Auth check #${checkAuthCount} completed. Current state:`, {
          isAuthenticated: isAuthenticated.value,
          hasUser: !!rawUser.value,
          initialCheckDone: initialAuthCheckDone.value,
        });
      }
    }
  }

  async function checkEmail(email: string, autoSendOtp = false): Promise<EmailCheckResponse> {
    logger.info("📧 Email check started for:", email);
    isLoading.value = true;
    error.value = undefined;

    try {
      const result = await authService.checkEmail(email, autoSendOtp);
      emailCheckResult.value = result;
      logger.info("📧 Email check result:", result);
      return result;
    } catch (err) {
      // Consumers (e.g. LoginPage) read `error` inline and surface it; keep this
      // silent so the shared handler doesn't double-toast over the caller.
      error.value = handleApiError(err, { fallbackMessage: "Failed to check email", silent: true }).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function sendOtp(email: string): Promise<boolean> {
    logger.info("🔑 OTP request started for:", email);
    isLoading.value = true;
    error.value = undefined;

    try {
      const result = await authService.sendOtp(email);
      logger.info("🔑 OTP request result:", result);
      if (!result.success) error.value = result.message;
      return result.success;
    } catch (err) {
      error.value = handleApiError(err, { fallbackMessage: "An error occurred sending OTP", silent: true }).message;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Additional state for profile completion
  // Reads from `rawUser` because profile completion is identity-based, not
  // role-based — the View-as-Member toggle must not affect this.
  const needsProfileCompletion = computed(() => {
    const explicitRequirement = rawUser.value?.requiresProfileCompletion;
    const incomplete =
      isAuthenticated.value &&
      rawUser.value &&
      (explicitRequirement ??
        (rawUser.value.profileComplete === false || rawUser.value.firstName === "" || rawUser.value.lastName === ""));

    if (incomplete) {
      logger.info("Profile completion needed - incomplete user profile", {
        accountMode: rawUser.value?.accountMode,
        firstName: rawUser.value?.firstName,
        lastName: rawUser.value?.lastName,
        profileComplete: rawUser.value?.profileComplete,
        requiresProfileCompletion: rawUser.value?.requiresProfileCompletion,
      });
    }

    return incomplete;
  });
  const tempUserId = ref<string | null>(null);

  async function loginWithOtp(
    email: string,
    otpCode: string,
  ): Promise<{
    success: boolean;
    needsAdditionalInfo: boolean;
  }> {
    logger.info("🔐 OTP login attempt started for:", email);
    isLoading.value = true;
    error.value = undefined;

    try {
      const result = await authService.otpLogin(email, otpCode);

      if (result.success) {
        if (result.needsAdditionalInfo) {
          logger.info("✅ OTP login successful, but needs profile completion");

          // Store user ID temporarily for profile completion
          if (result.user && result.user.id) {
            tempUserId.value = result.user.id;

            // Set preliminary user data so needsProfileCompletion computed property works
            rawUser.value = result.user;
          }

          // Set authenticated state
          isAuthenticated.value = true;

          return {
            success: true,
            needsAdditionalInfo: true,
          };
        } else {
          logger.info("✅ OTP login successful, fetching user data");
          // Update user data directly from response if available
          if (result.user) {
            rawUser.value = result.user;
            isAuthenticated.value = true;
          } else {
            // Or fetch it if not available in response
            await checkAuth(true);
          }

          if (!isAuthenticated.value) {
            logger.warn("⚠️ OTP login succeeded but user data fetch failed");
            error.value = "Login succeeded but failed to get user data.";
            return {
              success: false,
              needsAdditionalInfo: false,
            };
          }

          logger.info("🎉 OTP login complete and user authenticated");
          return {
            success: true,
            needsAdditionalInfo: false,
          };
        }
      } else {
        logger.info("❌ OTP login failed");
        error.value = "Invalid or expired OTP code";
        return {
          success: false,
          needsAdditionalInfo: false,
        };
      }
    } catch (err) {
      error.value = handleApiError(err, { fallbackMessage: "An error occurred during login", silent: true }).message;
      return {
        success: false,
        needsAdditionalInfo: false,
      };
    } finally {
      isLoading.value = false;
    }
  }

  async function completeUserProfile(firstName: string, lastName: string, password?: string): Promise<boolean> {
    logger.info("👤 Completing user profile");
    isLoading.value = true;
    error.value = undefined;

    if (!tempUserId.value && !rawUser.value?.id) {
      logger.error("❌ No user ID available for profile completion");
      error.value = "Cannot complete profile: missing user ID";
      isLoading.value = false;
      return false;
    }

    // Use stored tempUserId or get from current user
    const userId = tempUserId.value || rawUser.value!.id;

    try {
      const result = await authService.updateUserProfile(userId, firstName, lastName, password);

      if (result.success && result.user) {
        // Update user state with completed profile
        rawUser.value = result.user;
        isAuthenticated.value = true;
        tempUserId.value = null;

        logger.info("✅ Profile completed successfully");
        return true;
      } else {
        logger.info("❌ Failed to complete profile");
        error.value = result.message || "Failed to complete profile";
        return false;
      }
    } catch (err) {
      error.value = handleApiError(err, {
        fallbackMessage: "An error occurred completing profile",
        silent: true,
      }).message;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    logger.info("🔑 Login attempt started for:", email);
    isLoading.value = true;
    error.value = undefined;

    try {
      const success = await authService.login(email, password);

      if (success) {
        logger.info("✅ Login successful, fetching user data");
        // Fetch user data after successful login
        await checkAuth(true);

        if (!isAuthenticated.value) {
          logger.warn("⚠️ Login succeeded but user data fetch failed");
          error.value = "Login succeeded but failed to get user data.";
          return false;
        }

        logger.info("🎉 Login complete and user authenticated");
        return true;
      } else {
        logger.info("❌ Login failed");
        error.value = "Login failed. Please check your credentials.";
        return false;
      }
    } catch (err) {
      // Normalize + log with a request id, but stay silent: consumers read `error`
      // inline and surface it, and we branch on status below for the activation case.
      const apiError = handleApiError(err, { fallbackMessage: "An error occurred during login", silent: true });

      // A 401 for a pending activation, as opposed to a wrong password — the
      // status alone cannot tell them apart, so the backend sends a code
      // (#3174). This compared the message against a backend constant with
      // `===`, which a trailing full stop was enough to break.
      if (apiError.code === "ACCOUNT_NOT_ACTIVATED") {
        error.value = "Please click the verification link sent to your email.";
      } else {
        error.value = apiError.message;
      }
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<boolean> {
    logger.info("🚪 Logout started");
    isLoading.value = true;
    error.value = undefined;

    try {
      const success = await authService.logout();

      if (success) {
        logger.info("✅ Logout API call successful");
        // Clear user data immediately
        rawUser.value = undefined;
        isAuthenticated.value = false;
        logger.info("🚶 User logged out, state cleared");
        return true;
      } else {
        logger.info("❌ Logout API call failed");
        error.value = "Logout failed";
        return false;
      }
    } catch (err) {
      // Even if the API call fails, we should still clear the local state
      // to avoid a situation where user appears logged in but isn't
      rawUser.value = undefined;
      isAuthenticated.value = false;
      logger.info("⚠️ Logout failed but state cleared anyway");
      error.value = handleApiError(err, { fallbackMessage: "An error occurred during logout", silent: true }).message;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  // Admin read-only view-as (impersonation) actions (#2470). Both swap the browser
  // auth cookie to a different identity, so we hard-navigate to the returned path:
  // a full reload rebuilds every Pinia store against the new identity, avoiding
  // stale club/meeting state that a reactive in-place swap would leave behind.
  async function startViewAs(userId: number): Promise<void> {
    const result = await viewAsService.start(userId);
    window.location.assign(result.redirect || appHref("/home"));
  }

  async function stopViewAs(): Promise<void> {
    const result = await viewAsService.stop();
    window.location.assign(result.redirect || appHref("/home"));
  }

  return {
    // State
    user,
    rawUser,
    isAuthenticated,
    isLoading,
    error,
    authCheckFailed,
    initialAuthCheckDone,
    emailCheckResult,
    needsProfileCompletion,
    tempUserId,

    // Getters
    isAdmin,
    isBetaTester,
    hasBetaAccess,
    userClubs,
    userName,
    userHomeState,
    canViewAsMember,
    viewingAs,
    viewAsFailed,
    viewAsAdminEmail,
    viewAsTargetEmail,
    viewAsTargetId,

    // Actions
    checkAuth,
    startViewAs,
    stopViewAs,
    checkEmail,
    sendOtp,
    loginWithOtp,
    completeUserProfile,
    login,
    logout,
  };
});
